import React, { useState, useEffect, useMemo } from 'react';
import { apiService } from '../../services/apiService';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line,
} from 'recharts';
import {
  Zap, Database, BarChart3, Activity, CreditCard, Cpu, TrendingUp, Check, Minus,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { StatRow, Stat, StatSkeleton } from '../ui/StatRow';

interface UsageData {
  totalRequests: number;
  totalCreditsUsed: number;
  totalTokensUsed: number;
  apiKeysCount: number;
  activeKeysCount: number;
  total: number;
  dailyUsage: Record<string, { requests: number; credits: number; tokens: number }>;
  recentUsage: Array<{
    id: string;
    timestamp: Date;
    modelUsed: string;
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    creditsCost: number;
    taskType: string;
    endpoint: string;
    apiKeyId: string;
    webSearchUsed: boolean;
    reasoningUsed: boolean;
    hasImage: boolean;
    hasDocument: boolean;
    processingTimeMs: number;
    ipAddress: string;
    taskDeterminationUsed: boolean;
    complexity: number;
    functionCallingUsed: boolean;
  }>;
}

interface TDUsageData {
  plan: { displayName: string; freeTaskDetQuota: number };
  freeQuotaRemaining: number;
  currentPeriodCount: number;
  totalLifetime: number;
  nextResetDate: string | null;
  isAdmin: boolean;
}

const PAGE_SIZE = 25;

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="pk-sec">
      <span className="pk-label">{children}</span>
    </div>
  );
}

// ─── Usage ────────────────────────────────────────────────────────────────────

type Timeframe = '7d' | '30d' | 'all';

function getDateParams(timeframe: Timeframe): { dateFrom?: string; dateTo?: string } {
  if (timeframe === 'all') return {};
  const days = timeframe === '7d' ? 7 : 30;
  const from = new Date();
  from.setDate(from.getDate() - days);
  from.setHours(0, 0, 0, 0);
  return { dateFrom: from.toISOString(), dateTo: new Date().toISOString() };
}

export default function Usage() {
  const [data, setData] = useState<UsageData | null>(null);
  const [tdUsage, setTdUsage] = useState<TDUsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeframe, setTimeframe] = useState<Timeframe>('7d');
  const [modelFilter, setModelFilter] = useState('');
  const [page, setPage] = useState(1);

  // Per-session cache: timeframe → fetched data (cleared on page reload)
  const cache = React.useRef<Partial<Record<Timeframe, UsageData>>>({});

  // Initial load: fetch TD data once + 7d usage data
  useEffect(() => {
    const loadAll = async () => {
      try {
        const subscriptionsEnabled = process.env.REACT_APP_ENABLE_SUBSCRIPTIONS !== 'false';
        const [usageData, tdData] = await Promise.all([
          apiService.getUsageAnalytics(getDateParams('7d')),
          subscriptionsEnabled ? apiService.getTaskDetUsage().catch(() => null) : Promise.resolve(null),
        ]);
        cache.current['7d'] = usageData;
        setData(usageData);
        setTdUsage(tdData);
      } catch (err: any) {
        setError(err.message || 'Failed to load usage data');
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []);

  // Switch timeframe: use cache if available, otherwise fetch and cache
  useEffect(() => {
    if (loading) return; // skip during initial load
    if (cache.current[timeframe]) {
      setData(cache.current[timeframe]!);
      setPage(1);
      return;
    }
    const refetch = async () => {
      setTableLoading(true);
      setPage(1);
      try {
        const usageData = await apiService.getUsageAnalytics(getDateParams(timeframe));
        cache.current[timeframe] = usageData;
        setData(usageData);
      } catch (err: any) {
        setError(err.message || 'Failed to load usage data');
      } finally {
        setTableLoading(false);
      }
    };
    refetch();
  }, [timeframe]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset page when model filter changes
  useEffect(() => { setPage(1); }, [modelFilter]);

  // ── Derived values (all before any early return) ───────────────────────────

  const chartDays = timeframe === '7d' ? 7 : 30;

  // Compute period stats from recentUsage so they always match the table count
  const periodStats = useMemo(() => {
    return (data?.recentUsage ?? []).reduce(
      (acc, r) => ({
        requests: acc.requests + 1,
        credits: acc.credits + (r.creditsCost || 0),
        tokens: acc.tokens + (r.totalTokens || 0),
      }),
      { requests: 0, credits: 0, tokens: 0 }
    );
  }, [data]);

  const avgCostPerRequest = periodStats.requests > 0
    ? (periodStats.credits / periodStats.requests).toFixed(6)
    : '0.000000';

  const chartData = useMemo(() => {
    if (!data?.dailyUsage) return [];
    if (timeframe === 'all') {
      return Object.entries(data.dailyUsage)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([dateStr, b]) => {
          const d = new Date(dateStr);
          return {
            date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            requests: b.requests,
            credits: b.credits,
          };
        });
    }
    return Array.from({ length: chartDays }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (chartDays - 1 - i));
      const key = d.toISOString().split('T')[0];
      return {
        date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        requests: data.dailyUsage[key]?.requests || 0,
        credits: data.dailyUsage[key]?.credits || 0,
      };
    });
  }, [data, timeframe, chartDays]);

  // All records already date-filtered by server — just apply model filter client-side
  const filteredUsage = useMemo(() => {
    const all = data?.recentUsage ?? [];
    if (!modelFilter) return all;
    return all.filter(u => u.modelUsed === modelFilter);
  }, [data, modelFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredUsage.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginatedUsage = filteredUsage.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Unique models from all fetched records (for dropdown)
  const allModels = useMemo(() => {
    const models = new Set((data?.recentUsage ?? []).map(u => u.modelUsed).filter(Boolean));
    return Array.from(models).sort();
  }, [data]);

  // Top models from all fetched records (already date-filtered by server)
  const topModels = useMemo(() => {
    const counts = new Map<string, number>();
    (data?.recentUsage ?? []).forEach(u => {
      if (u.modelUsed) counts.set(u.modelUsed, (counts.get(u.modelUsed) ?? 0) + 1);
    });
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [data]);

  const topModelMax = topModels[0]?.[1] ?? 1;

  // TD helpers
  const freeQuotaTotal = tdUsage?.plan?.freeTaskDetQuota ?? 0;
  const freeQuotaUsed = freeQuotaTotal - (tdUsage?.freeQuotaRemaining ?? 0);

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const planLabel = tdUsage?.isAdmin ? 'Unlimited' : (tdUsage?.plan?.displayName ?? '—');

  const timeframeLabel: Record<Timeframe, string> = { '7d': '7 days', '30d': '30 days', 'all': 'All' };

  if (error) {
    return (
      <div className="pk-note pk-note-danger">
        Error: {error}
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="pk-page-head">
        <div className="col">
          <h1 className="pk-h1">Usage Analytics</h1>
          <p className="pk-sub">Track your API usage and spending patterns</p>
        </div>
        <div className="actions">
          <div className="pk-seg">
            {(['7d', '30d', 'all'] as Timeframe[]).map(tf => (
              <button
                key={tf}
                type="button"
                aria-pressed={timeframe === tf}
                className={timeframe === tf ? 'is-active' : ''}
                onClick={() => setTimeframe(tf)}
              >
                {timeframeLabel[tf]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Task Determinations */}
      <SectionLabel>Task Determinations</SectionLabel>
      <div style={{ marginBottom: 24 }}>
        <StatRow>
          {loading ? Array.from({ length: 4 }).map((_, i) => <StatSkeleton key={i} />) : (
            <>
              <Stat
                icon={<Zap size={17} />}
                title="Current Plan"
                value={
                  tdUsage?.isAdmin
                    ? <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>Unlimited <span className="pk-badge pk-badge-accent">Admin</span></span>
                    : planLabel
                }
                subtitle={tdUsage?.nextResetDate ? `Resets ${formatDate(tdUsage.nextResetDate)}` : 'Manage subscription'}
              />
              <Stat
                icon={<Database size={17} />}
                title="Free Quota"
                value={tdUsage ? `${(tdUsage.freeQuotaRemaining ?? 0).toLocaleString()} left` : '—'}
                subtitle={freeQuotaTotal > 0 ? `of ${freeQuotaTotal.toLocaleString()} this period` : 'This billing period'}
                progress={freeQuotaTotal > 0 ? { used: freeQuotaUsed, total: freeQuotaTotal } : undefined}
              />
              <Stat
                icon={<BarChart3 size={17} />}
                title="Period Task Dets."
                value={tdUsage ? (tdUsage.currentPeriodCount ?? 0).toLocaleString() : '—'}
                subtitle="This billing period"
              />
              <Stat
                icon={<Activity size={17} />}
                title="Lifetime Task Dets."
                value={tdUsage ? (tdUsage.totalLifetime ?? 0).toLocaleString() : '—'}
                subtitle="All-time total"
              />
            </>
          )}
        </StatRow>
      </div>

      {/* API Usage */}
      <SectionLabel>API Usage</SectionLabel>
      <div style={{ marginBottom: 24 }}>
        <StatRow>
          {loading ? Array.from({ length: 4 }).map((_, i) => <StatSkeleton key={i} />) : (
            <>
              <Stat
                icon={<TrendingUp size={17} />}
                title="API Requests"
                value={tableLoading ? '…' : periodStats.requests.toLocaleString()}
                subtitle="In selected period"
              />
              <Stat
                icon={<CreditCard size={17} />}
                title="Credits Used"
                value={tableLoading ? '…' : `$${periodStats.credits.toFixed(4)}`}
                subtitle="In selected period"
              />
              <Stat
                icon={<Cpu size={17} />}
                title="Tokens Used"
                value={tableLoading ? '…' : periodStats.tokens.toLocaleString()}
                subtitle="In selected period"
              />
              <Stat
                icon={<BarChart3 size={17} />}
                title="Avg Cost / Request"
                value={tableLoading ? '…' : `$${avgCostPerRequest}`}
                subtitle="Per API call"
              />
            </>
          )}
        </StatRow>
      </div>

      {/* Charts + Most Used Models */}
      {!loading && (
        <div className="pk-grid pk-grid-3" style={{ marginBottom: 24 }}>
          <div className="pk-card">
            <h3 className="pk-h4" style={{ marginBottom: 16 }}>Daily Requests</h3>
            {tableLoading ? (
              <div style={{ height: 220, display: 'grid', placeItems: 'center' }}>
                <div className="pk-skel" style={{ width: '100%', height: '100%', borderRadius: 'var(--arc-r-md)' }} />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--arc-line-2)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--arc-ink-3)' }} tickLine={false} stroke="var(--arc-line)" />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--arc-ink-3)' }} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ background: 'var(--arc-paper)', border: '1px solid var(--arc-line)', borderRadius: 8, fontSize: 12.5 }} />
                  <Bar dataKey="requests" fill="var(--arc-accent)" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="pk-card">
            <h3 className="pk-h4" style={{ marginBottom: 16 }}>Daily Credits Used</h3>
            {tableLoading ? (
              <div style={{ height: 220, display: 'grid', placeItems: 'center' }}>
                <div className="pk-skel" style={{ width: '100%', height: '100%', borderRadius: 'var(--arc-r-md)' }} />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--arc-line-2)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--arc-ink-3)' }} tickLine={false} stroke="var(--arc-line)" />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--arc-ink-3)' }} tickLine={false} axisLine={false} />
                  <Tooltip
                    formatter={(value) => [`$${Number(value).toFixed(4)}`, 'Credits']}
                    contentStyle={{ background: 'var(--arc-paper)', border: '1px solid var(--arc-line)', borderRadius: 8, fontSize: 12.5 }}
                  />
                  <Line type="monotone" dataKey="credits" stroke="var(--arc-success)" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Most Used Models */}
          <div className="pk-card">
            <h3 className="pk-h4" style={{ marginBottom: 16 }}>Most Used Models</h3>
            {tableLoading ? (
              <div style={{ height: 220, display: 'grid', placeItems: 'center' }}>
                <div className="pk-skel" style={{ width: '100%', height: '100%', borderRadius: 'var(--arc-r-md)' }} />
              </div>
            ) : topModels.length === 0 ? (
              <div className="pk-empty-inline" style={{ height: 220 }}>
                No data in this period
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
                {topModels.map(([model, count]) => (
                  <div key={model}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                      <span className="pk-small" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '75%' }} title={model}>{model}</span>
                      <span className="pk-small pk-num" style={{ color: 'var(--arc-ink)', fontWeight: 700, marginLeft: 8 }}>{count}</span>
                    </div>
                    <div style={{ width: '100%', height: 5, borderRadius: 999, background: 'var(--arc-line-2)', overflow: 'hidden' }}>
                      <div
                        style={{
                          height: '100%', borderRadius: 999, background: 'var(--arc-accent)',
                          width: `${Math.round((count / topModelMax) * 100)}%`, transition: 'width var(--arc-t) var(--arc-ease)',
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* API Calls Table */}
      {!loading && (
        <div className="pk-table-wrap pk-table-grad">
          <div className="pk-card-head">
            <div>
              <h3 className="pk-h4">API Calls</h3>
              <p className="pk-small">
                {tableLoading ? 'Loading…' : `${filteredUsage.length.toLocaleString()} call${filteredUsage.length !== 1 ? 's' : ''} in selected period`}
              </p>
            </div>
            {allModels.length > 0 && (
              <div className="actions">
                <div className="pk-select-wrap">
                  <select
                    value={modelFilter}
                    onChange={e => setModelFilter(e.target.value)}
                    className="pk-select pk-small"
                    style={{ minHeight: 32, paddingTop: 4, paddingBottom: 4 }}
                  >
                    <option value="">All models</option>
                    {allModels.map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          <div className="pk-table-scroll" style={{ minHeight: 320 }}>
            {tableLoading ? (
              <div style={{ height: 320, display: 'grid', placeItems: 'center' }}>
                <div className="pk-skel" style={{ width: '92%', height: '80%', borderRadius: 'var(--arc-r-md)' }} />
              </div>
            ) : filteredUsage.length === 0 ? (
              <div className="pk-empty" style={{ border: 0, borderRadius: 0 }}>
                <div className="pk-i-plate"><BarChart3 size={18} /></div>
                <p className="pk-sub">No API calls in this period</p>
              </div>
            ) : (
              <table className="pk-table">
                <thead>
                  <tr>
                    {['Date', 'Model', 'Input Tokens', 'Output Tokens', 'Cost', 'Complexity', 'Task Det.', 'Features'].map(h => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsage.map((usage, index) => (
                    <tr key={usage.id || index}>
                      <td className="pk-small">
                        {new Date(usage.timestamp).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td>
                        <span className="pk-badge pk-badge-outline">{usage.modelUsed}</span>
                      </td>
                      <td className="num">{usage.inputTokens.toLocaleString()}</td>
                      <td className="num">{usage.outputTokens.toLocaleString()}</td>
                      <td className="num">
                        {usage.creditsCost === 0
                          ? <span className="pk-badge pk-badge-success">Free</span>
                          : <span className="strong">${usage.creditsCost.toFixed(6)}</span>
                        }
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {usage.complexity > 0 ? usage.complexity : <Minus size={14} className="pk-cross" style={{ margin: '0 auto' }} />}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {usage.taskDeterminationUsed
                          ? <Check size={14} className="pk-tick" style={{ margin: '0 auto' }} />
                          : <Minus size={14} className="pk-cross" style={{ margin: '0 auto' }} />
                        }
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {usage.webSearchUsed && <span className="pk-badge pk-badge-outline">Search</span>}
                          {usage.reasoningUsed && <span className="pk-badge pk-badge-outline">Reasoning</span>}
                          {usage.hasImage && <span className="pk-badge pk-badge-outline">Image</span>}
                          {usage.hasDocument && <span className="pk-badge pk-badge-outline">Doc</span>}
                          {usage.functionCallingUsed && <span className="pk-badge pk-badge-outline">Functions</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {!tableLoading && totalPages > 1 && (
            <div className="pk-pag">
              <span>
                Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filteredUsage.length)} of {filteredUsage.length.toLocaleString()}
              </span>
              <div className="spacer" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <button
                  type="button"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="pk-btn pk-btn-quiet pk-btn-icon pk-btn-sm"
                >
                  <ChevronLeft size={15} />
                </button>
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  // show pages around current page
                  let p = i + 1;
                  if (totalPages > 7) {
                    if (safePage <= 4) p = i + 1;
                    else if (safePage >= totalPages - 3) p = totalPages - 6 + i;
                    else p = safePage - 3 + i;
                  }
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPage(p)}
                      className={p === safePage ? 'pk-btn pk-btn-sm' : 'pk-btn pk-btn-quiet pk-btn-sm'}
                      style={{ width: 28, padding: 0 }}
                    >
                      {p}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="pk-btn pk-btn-quiet pk-btn-icon pk-btn-sm"
                >
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
